// Bid Modifier Cleanup — Bidding & Budgets section, LLM judgment row.
// Renders Claude's verdict on whether the modifier mix is coherent with each campaign's
// bidding strategy. Supporting table groups modifiers by campaign with explicit Smart-Bidding
// strategy badging so the auditor can see at a glance which campaigns carry legacy modifier
// debt despite running Smart Bidding.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface ModifierRow {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  channelType?: string;
  type?: string;
  criterionDetail?: string;
  bidModifier: number;
}

interface CampaignGroup {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  channelType?: string;
  modifierCount: number;
  modifiers: ModifierRow[];
}

interface BidModifierShape {
  totalModifiers: number;
  campaignsWithModifiers?: number;
  byCampaign: CampaignGroup[];
  allModifiers: ModifierRow[];
}

// Smart-Bidding strategies that largely IGNORE manual bid modifiers — campaigns on these
// strategies with modifiers are likely candidates for cleanup.
const SMART_BIDDING = new Set([
  'TARGET_CPA',
  'TARGET_ROAS',
  'MAXIMIZE_CONVERSIONS',
  'MAXIMIZE_CONVERSION_VALUE'
]);

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

const MODIFIER_TYPE_LABEL: Record<string, string> = {
  DEVICE: 'Device',
  AD_SCHEDULE: 'Schedule',
  AGE_RANGE: 'Age',
  GENDER: 'Gender',
  INCOME_RANGE: 'Income',
  USER_LIST: 'Audience'
};

function modifierTypeLabel(t: string | undefined): string {
  if (!t) return '—';
  return MODIFIER_TYPE_LABEL[t] ?? t;
}

function formatModifier(n: number): string {
  const pctOf100 = Math.round((n - 1) * 100);
  if (pctOf100 === 0) return '0%';
  return `${pctOf100 > 0 ? '+' : ''}${pctOf100}%`;
}

export const BidModifierCleanupEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as BidModifierShape;

  if (e.totalModifiers === 0) {
    return (
      <div className='evidence'>
        <p className='evidence__lead'>
          No non-default bid modifiers detected — bidding configuration is clean.
        </p>
      </div>
    );
  }

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Campaigns with non-default bid modifiers
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.byCampaign.length} campaigns · {e.totalModifiers} modifiers
          </span>
        </h4>
        <table className='evidence__table evidence__table--bid-modifiers'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th className='evidence__th--right'>Modifiers</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {e.byCampaign.map(c => {
              const smartBidding = c.biddingStrategy ? SMART_BIDDING.has(c.biddingStrategy) : false;
              return (
                <tr
                  key={c.campaignId ?? c.campaignName}
                  className={classNames({ 'evidence__row--flagged': smartBidding })}
                >
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td>
                    {strategyLabel(c.biddingStrategy)}
                    {smartBidding && (
                      <span className='evidence__inline-note'> (Smart Bidding)</span>
                    )}
                  </td>
                  <td className='evidence__td--right'>{c.modifierCount.toLocaleString()}</td>
                  <td>
                    <span className='evidence__modifier-list'>
                      {c.modifiers.map((m, i) => (
                        <span key={i} className='evidence__modifier-chip'>
                          {modifierTypeLabel(m.type)}/{m.criterionDetail ?? '?'}{' '}
                          <strong>{formatModifier(m.bidModifier)}</strong>
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Smart Bidding strategies (Target CPA, Target ROAS, Max Conversions, Max Conv. Value) largely
        ignore manual bid modifiers — campaigns on those strategies with extensive non-audience
        modifiers are usually leftover Manual-CPC artifacts. Device modifiers on Target Impression
        Share and audience modifiers on observation audiences remain meaningful.
      </p>
    </div>
  );
};
